import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { HighlightBarChart, SegmentedBreakdown } from "@/components/data-display/viz";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { PermissionGate } from "@/components/permission/permission-gate";
import { aumUnvaluedWalletLabel, getAumTotalDisplay, getTopWallets } from "../dashboard-selectors";
import type { WalletTypeItem } from "../dashboard-selectors";
import type { Wallet } from "@/types";
import { DASHBOARD_TOP_WALLET_LIMIT } from "../dashboard-data";
import {
  formatUsdValue,
  usdCompact,
  walletValuationValue,
  type ValuationSummary,
} from "@/features/valuation/valuation";
import { TYPE_FILL, TYPE_FILL_FALLBACK, TYPE_SLOT } from "../chart-slots";

/**
 * ALL-5825 (UX audit D13): "Balance by wallet" used to draw one ghost bar per
 * funded wallet — 55 bars in a 55-wallet org, unreadable at tile width. At most
 * this many of the largest wallets are charted; every wallet the chart does not
 * draw is stated in words beneath it, so the tile never implies the top eight
 * are the whole portfolio.
 */
export const BALANCE_BAR_LIMIT = 8;

export function AumComposition({
  items,
  total,
  wallets,
}: {
  items: WalletTypeItem[];
  total: ValuationSummary;
  wallets: Wallet[];
}) {
  const topWallets = getTopWallets(wallets, DASHBOARD_TOP_WALLET_LIMIT);
  const totalDisplay = getAumTotalDisplay(total);

  // Only wallets with a current valuation can be ranked or charted; `null` is
  // unknown, not zero (pipeline G6), so an unvalued wallet is never drawn as a
  // zero-height bar or listed at the bottom of a ranking it did not enter.
  const funded = wallets
    .filter((wallet) => (walletValuationValue(wallet) ?? 0) > 0)
    .sort((a, b) => (walletValuationValue(b) ?? 0) - (walletValuationValue(a) ?? 0));

  // The chart draws the largest `BALANCE_BAR_LIMIT` funded wallets. Everything
  // left over is named below the chart: a wallet with no valuation still counts
  // as a wallet the chart is not showing, and the second count says why those
  // cannot be drawn (unknown is not zero — pipeline G6).
  const chartedWallets = funded.slice(0, BALANCE_BAR_LIMIT);
  const remainingWalletCount = wallets.length - chartedWallets.length;
  const notPricedWalletCount = wallets.filter((wallet) => walletValuationValue(wallet) == null).length;

  if (wallets.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">AUM Composition</h2>
        <div className="flex flex-1 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line px-4 text-center text-sm text-muted">
          <p>No wallets yet.</p>
          {/* The create action is offered only to a session the backend will
              actually allow — a read-only role was told to "create a wallet"
              by copy it could never act on (same gate as wallets.tsx:177). */}
          <PermissionGate
            permission="wallet:create"
            fallback={<p>AUM composition appears here once a wallet exists.</p>}
          >
            <p>
              <Link to="/wallets/new" className="font-semibold text-primary underline">
                Create a wallet
              </Link>{" "}
              to see AUM composition here.
            </p>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">AUM Composition</h2>
          <div className="mt-0.5 text-xs text-muted">By wallet type</div>
        </div>
        <Link to="/wallets" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          Wallets <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col gap-4">
        {items.length > 0 ? (
          <div>
            <SegmentedBreakdown
              data={items.map((item) => ({
                label: item.type,
                value: item.value,
                // The selector's share, not a recomputed `value / sum(items)`:
                // the denominator is the wallet total, so a type is drawn at its
                // real proportion of AUM even when it is the only row rendered.
                pct: item.pct,
                fill: TYPE_FILL[item.type] ?? TYPE_FILL_FALLBACK,
                slot: TYPE_SLOT[item.type] ?? 5,
              }))}
              format={(value) => usdCompact.format(value)}
            />
            <div className="mt-2 text-xs text-muted">
              across {funded.length} funded wallet{funded.length === 1 ? "" : "s"} · {items.length} wallet
              type{items.length === 1 ? "" : "s"}
              {/* The hero KPI's own sentence, off the same summary field, so
                  the two AUM figures state the same exclusions instead of
                  leaving this one looking like a complete portfolio. */}
              {total.unvaluedWalletCount > 0
                ? ` · ${aumUnvaluedWalletLabel(total.unvaluedWalletCount)}`
                : ""}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted">
            No wallet type carries a valued balance yet, so there is nothing to break down.
          </p>
        )}

        <div className="flex min-h-0 flex-1 flex-col border-t border-line pt-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-muted">
              Balance by wallet
            </span>
            {funded.length > 0 && <span className="text-[11px] text-muted">Hover a bar for details</span>}
          </div>
          {funded.length > 0 ? (
            <>
              <div className="min-h-0 flex-1">
                <HighlightBarChart
                  data={chartedWallets.map((wallet) => ({
                    label: wallet.name,
                    detail: wallet.chain,
                    value: walletValuationValue(wallet) ?? 0,
                  }))}
                  format={(value) => usdCompact.format(value)}
                  avgLabel={(avg) => `Avg ${usdCompact.format(avg)}`}
                  defaultActive={0}
                  height={230}
                  fill
                  ariaLabel="Balance by wallet"
                />
              </div>
              {/* The final row of the chart, in words: the wallets the eight bars
                  do not show, and the ones with no valuation to show. Neither is
                  drawn as a zero-height bar. */}
              {(remainingWalletCount > 0 || notPricedWalletCount > 0) && (
                <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-muted">
                  {remainingWalletCount > 0 && (
                    <span data-testid="balance-by-wallet-more">
                      +{remainingWalletCount} more wallet{remainingWalletCount === 1 ? "" : "s"}
                    </span>
                  )}
                  {notPricedWalletCount > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <span>{notPricedWalletCount} not priced</span>
                      <UnavailableValue />
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-muted">
              No wallet has a current valuation, so there is no balance to chart.
            </p>
          )}
        </div>

        <div className="rounded-xl border border-line bg-bg p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Top wallets</div>
          {topWallets.length > 0 ? (
            <div className="space-y-2">
              {topWallets.map((w) => (
                <Link
                  key={w.id}
                  to={`/wallets/${w.id}`}
                  className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface"
                >
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-ink">{w.name}</div>
                    <div className="text-[11px] text-muted">{w.chain}</div>
                  </div>
                  <div className="shrink-0 text-xs font-semibold text-ink">
                    {formatUsdValue(w.balanceUsd)}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted">
              No wallet has a current valuation, so no wallet can be ranked here yet.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl bg-bg px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs text-muted">Total AUM</div>
            <div className="text-lg font-bold text-ink">{totalDisplay.value}</div>
            {/* Unavailable keeps its label and reason together in one run, so the
                tile does not repeat the hero KPI's `Valuation unavailable` line. */}
            {totalDisplay.unavailable && totalDisplay.reason && (
              <div className="text-[11px] text-muted">{`${totalDisplay.label} — ${totalDisplay.reason}`}</div>
            )}
          </div>
          {!totalDisplay.unavailable && (
            <div className="shrink-0 text-right text-xs text-muted">
              {totalDisplay.label ?? `${items.length} wallet type${items.length === 1 ? "" : "s"}`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
